import React from 'react';
import { Link } from 'react-router-dom';
import type { CountryCode } from '../../config/countries';
import { PRODUCT_LABELS } from '../../config/products';
import type { DataProductId } from '../../config/products';
import { isChoosable, useCoverage } from '../../hooks/useCoverage';
import NavBar from '../common/NavBar';
import CountryCoverageRow from './CountryCoverageRow';
import DeliveryLegend from './DeliveryLegend';

interface CountryChooserProps {
  product: DataProductId;
  title: string;
  onChoose(c: CountryCode): void;
}

/** The first screen of a data product: every country, what has been delivered, and which can be opened. */
const CountryChooser: React.FC<CountryChooserProps> = ({ product, title, onChoose }) => {
  const { rows, loading, retry } = useCoverage(product);
  const list = rows[product];
  const nothingToChoose = !loading && list.every((r) => !isChoosable(r.status));

  return (
    <div className="min-h-screen bg-paper-100">
      <NavBar title={title} showBackButton backTo="/" />
      <main className="mx-auto w-full max-w-3xl p-4">
        <h1 className="text-3xl font-semibold text-ink">Choose a country</h1>
        <p className="text-ink-muted">
          {`Pick where to use ${PRODUCT_LABELS[product]}. Each bar is one day of data for your account.`}
        </p>
        <DeliveryLegend className="mt-3" />

        <div role="status" className={nothingToChoose ? 'mt-4 text-ink-muted' : undefined}>
          {loading ? (
            <span className="sr-only">Checking deliveries</span>
          ) : nothingToChoose ? (
            'None of these countries has data for your account yet.'
          ) : null}
        </div>

        <ul
          aria-label={`Countries for ${PRODUCT_LABELS[product]}`}
          className="mt-4 divide-y divide-paper-300 rounded-2xl border border-paper-300 bg-surface"
        >
          {list.map((row) => (
            <CountryCoverageRow
              key={row.country}
              country={row.country}
              product={product}
              status={row.status}
              onChoose={onChoose}
              onRetry={() => retry(row.country)}
            />
          ))}
        </ul>

        {nothingToChoose ? (
          <Link to="/data-coverage" className="link link-primary mt-1 inline-block">
            See all data coverage
          </Link>
        ) : null}
      </main>
    </div>
  );
};

export default CountryChooser;
